import React, { useState, useRef, useEffect } from 'react';
import { Box, ToggleButtonGroup, ToggleButton } from '@mui/material';
import NumericRangeFilter from 'components/filters/types/NumericRangeFilter';
import DateRangeFilter from 'components/filters/types/DateRangeFilter';
import SQLFilter from 'components/filters/types/SQLFilter';
import BooleanFilter from 'components/filters/types/BooleanFilter';
import SelectValuesFilter from 'components/filters/types/SelectValuesFilter';

/**
 * FilterFactory handles rendering the appropriate filter UI based on column type.
 * 
 * Key behaviors:
 * 1. String columns: Toggle between 'sql' mode (SQL WHERE clause) and 'select' mode (dropdown with values)
 * 2. Non-string columns: Toggle between 'friendly' mode (specialized UI) and 'sql' mode (SQL WHERE clause)
 * 3. initialFilterMode can override the default mode for both types
 */
const FilterFactory = ({ 
  columnType, 
  value, 
  onChange, 
  error,
  onValidationErrorChange,
  projectId,
  datasetId,
  tableId,
  columnName,
  initialFilterMode
}) => {
  // IMPORTANT: The component uses two separate state variables to track filter modes:
  // - stringFilterMode: Used only for string columns (options: 'sql' or 'select')
  // - nonStringFilterMode: Used for all other column types (options: 'friendly' or 'sql')
  // 
  // Both are initialized with initialFilterMode if provided, otherwise default to 'sql'
  // When initialFilterMode changes OR when columnType changes, update these states
  const [stringFilterMode, setStringFilterMode] = useState(
    initialFilterMode || 'sql'
  );
  const [nonStringFilterMode, setNonStringFilterMode] = useState(
    initialFilterMode || 'sql'
  );

  // Update filter modes when initialFilterMode or columnType changes
  useEffect(() => {
    if (initialFilterMode) {
      if (columnType.toLowerCase() === 'string') {
        setStringFilterMode(initialFilterMode);
      } else {
        setNonStringFilterMode(initialFilterMode);
      }
    }
  }, [initialFilterMode, columnType]);

  // Add state to track filter validation errors
  const [filterValidationError, setFilterValidationError] = useState(false);
  const prevValidationState = useRef(false);
  const lastValue = useRef(value);

  // Determine if this type has a friendly UI (excluding string type which has its own toggle)
  const hasFriendlyUI = (type) => {
    const friendlyTypes = [
      'integer',
      'float',
      'numeric',
      'date',
      'datetime',
      'timestamp',
      'bool',
      'boolean'
    ];
    return friendlyTypes.includes(type.toLowerCase());
  };

  /**
   * Selects the appropriate filter component based on column type and current mode.
   * Different logic is applied for string vs non-string columns:
   * 
   * 1. For string columns:
   *    - Uses stringFilterMode state to determine component
   *    - 'sql' mode → SQLFilter
   *    - 'select' mode → SelectValuesFilter
   * 
   * 2. For non-string columns:
   *    - Uses nonStringFilterMode state to determine component
   *    - 'sql' mode → SQLFilter (regardless of column type)
   *    - 'friendly' mode → Type-specific filter components
   */
  const getFilterComponent = () => {
    const type = columnType.toLowerCase();
    
    // String columns use stringFilterMode to choose between SQL and Select Values
    if (type === 'string') {
      return stringFilterMode === 'sql' ? SQLFilter : SelectValuesFilter;
    }

    // For non-string columns, first check if we're in SQL mode
    if (nonStringFilterMode === 'sql') {
      return SQLFilter;
    }

    // If we're in 'friendly' mode, choose appropriate specialized component
    switch (type) {
      case 'integer':
      case 'float':
      case 'numeric':
        return NumericRangeFilter;
      case 'date':
      case 'datetime':
      case 'timestamp':
        return DateRangeFilter;
      case 'bool':
      case 'boolean':
        return BooleanFilter;
      default:
        return SQLFilter; // Default to SQL input for unsupported types
    }
  };

  const FilterComponent = getFilterComponent();

  /**
   * When filter value changes, we need to pass both the value and current mode
   * to the parent component. We determine which mode state to use based on column type.
   */
  const handleFilterChange = (newValue, mode) => {
    // Skip update if the value hasn't changed to prevent infinite loops
    if (newValue === lastValue.current) {
      return;
    }
    
    lastValue.current = newValue;
    
    // For string columns, use stringFilterMode; for others, use nonStringFilterMode
    // If mode is provided in the callback, use that instead (this helps prevent mode mismatch)
    const currentMode = mode || (columnType.toLowerCase() === 'string' 
      ? stringFilterMode 
      : nonStringFilterMode);
    
    // Update mode state based on the mode parameter
    if (mode && mode !== currentMode) {
      if (columnType.toLowerCase() === 'string') {
        setStringFilterMode(mode);
      } else {
        setNonStringFilterMode(mode);
      }
    }
    
    onChange(newValue, currentMode);
  };

  // Handler for validation errors from child filters
  const handleFilterValidationError = (hasError) => {
    // Only update state and propagate if the error state has changed
    if (filterValidationError !== hasError) {
      setFilterValidationError(hasError);
      
      // Propagate up to parent only if the state changed
      if (onValidationErrorChange && prevValidationState.current !== hasError) {
        onValidationErrorChange(hasError);
        prevValidationState.current = hasError;
      }
    }
  };

  // Sync validation errors with parent when component mounts or updates
  useEffect(() => {
    if (error !== undefined && prevValidationState.current !== error) {
      setFilterValidationError(!!error);
      prevValidationState.current = !!error;
    }
  }, [error]);
  
  // Track value changes
  useEffect(() => {
    lastValue.current = value;
  }, [value]);

  const toggleButtonStyles = {
    backgroundColor: '#fafafa',
    color: '#00695c',
    border: '1px solid #116c6e',
    '&:hover': {
      backgroundColor: '#e0e0e0',
    },
    '&.Mui-selected': {
      color: '#ffffff',
      backgroundColor: '#116c6e',
      '&:hover': {
        backgroundColor: '#e0e0e0', 
        color: '#00695c',
      },
    },
  };

  return (
    <Box>
      {/* 
        Toggle UI for string columns:
        - Uses stringFilterMode state
        - Options are 'sql' (WHERE clause) or 'select' (dropdown selection)
      */}
      {columnType.toLowerCase() === 'string' && (
        <ToggleButtonGroup
          value={stringFilterMode}
          exclusive
          onChange={(e, newValue) => {
            if (newValue !== null) {
              setStringFilterMode(newValue);
              // Reset value when mode changes, but don't trigger unnecessary rerenders
              if (value) {
                lastValue.current = '';
                onChange('', newValue);
              }
            }
          }}
          size="small"
          sx={{ mb: 1 }}
        >
          <ToggleButton value="sql">
            SQL WHERE
          </ToggleButton>
          <ToggleButton value="select">
            Select Values
          </ToggleButton>
        </ToggleButtonGroup>
      )}

      {/* 
        Toggle UI for non-string columns with friendly UI options:
        - Uses nonStringFilterMode state
        - Options are 'friendly' (specialized UI) or 'sql' (WHERE clause)
      */}
      {hasFriendlyUI(columnType) && columnType.toLowerCase() !== 'string' && (
        <ToggleButtonGroup
          value={nonStringFilterMode}
          exclusive
          onChange={(e, newValue) => {
            if (newValue !== null) {
              setNonStringFilterMode(newValue);
              // Reset value when mode changes, but don't trigger unnecessary rerenders
              if (value) {
                lastValue.current = '';
                onChange('', newValue);
              }
            }
          }}
          size="small"
          sx={{ mb: 1 }}
        >
          <ToggleButton value="friendly" sx={toggleButtonStyles}>
            EZ Filter
          </ToggleButton>
          <ToggleButton value="sql" sx={toggleButtonStyles}>
            SQL WHERE
          </ToggleButton>
        </ToggleButtonGroup>
      )}

      {/* 
        Render the selected filter component with appropriate props.
        The nonStringFilterMode prop uses either stringFilterMode or nonStringFilterMode
        depending on column type.
      */}
      <FilterComponent
        value={value}
        onChange={handleFilterChange}
        error={error}
        onValidationErrorChange={handleFilterValidationError}
        projectId={projectId}
        datasetId={datasetId}
        tableId={tableId}
        columnName={columnName}
        filterMode={columnType.toLowerCase() === 'string' ? stringFilterMode : nonStringFilterMode}
      />
    </Box>
  );
};

export default FilterFactory; 